import { useEffect, useRef, useState } from "react";

export interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Extracts H2/H3 headings from a container and tracks the active one via IntersectionObserver.
 * Pass a `contentKey` that changes when the container content changes (e.g. the markdown string)
 * so the hook re-scans headings after render.
 */
export function useTableOfContents(
  containerRef: React.RefObject<HTMLElement | null>,
  contentKey?: string
) {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const timer = setTimeout(() => {
      const headings = container.querySelectorAll<HTMLHeadingElement>("h2, h3");
      const tocItems: TocItem[] = [];

      headings.forEach((heading, index) => {
        if (!heading.id) {
          heading.id = `heading-${index}-${
            heading.textContent
              ?.slice(0, 30)
              .replace(/\s+/g, "-")
              .replace(/[^a-zA-Z0-9-]/g, "")
              .toLowerCase() || index
          }`;
        }
        tocItems.push({
          id: heading.id,
          text: heading.textContent || "",
          level: heading.tagName === "H2" ? 2 : 3,
        });
      });

      setItems(tocItems);

      if (tocItems.length === 0) return;

      observerRef.current?.disconnect();

      const observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries.filter((e) => e.isIntersecting);
          if (visibleEntries.length > 0) {
            setActiveId(visibleEntries[0].target.id);
          }
        },
        {
          rootMargin: "-80px 0px -70% 0px",
          threshold: 0,
        }
      );

      observerRef.current = observer;
      headings.forEach((h) => observer.observe(h));
    }, 100);

    return () => {
      clearTimeout(timer);
      observerRef.current?.disconnect();
    };
  }, [containerRef, contentKey]);

  return { items, activeId };
}
